<template>
  <div class="contacts">
    <page-header :title="title" :title-in-breadcrumbs="titleInBreadcrumbs" />
    <template v-if="isAddressPage">
      <div class="public-container public-container-mobile">
        <contacts-filials class="mt-50 mb-110"/>
      </div>
      <terminals-geo @terminal-select="onClick"/>
    </template>
    <template v-else>
      <div class="public-container public-container-mobile">
        <contacts-terminals
          :has-title="isAddressLocationPage" 
          :location-guid="contactCity?.guid"
        >
          <contacts-departments />
        </contacts-terminals>
        <popular-directions :title="popularTitle"/>
        <div class="contacts-seo" v-if="isAddressLocationPage && seo?.seo?.text" v-html="seo.seo.text" />
        <public-service-links-grid
          v-if="isAddressLocationPage"
          :items="items"
          text-only
          class="my-90"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { Location } from '~/types/types'
import { useCityStore } from '~/stores/city'
import { useSeoStore } from '~/stores/seo'
import PageHeader from '~/components/public/page-header.vue'
import ContactsTerminals from '~/components/public/contacts/contacts-terminals.vue'
import ContactsDepartments from '~/components/public/contacts/contacts-departments.vue'
import PopularDirections from '~/components/public/popular-directions/popular-directions.vue'
import { until } from '@vueuse/shared';
import { useTerminalStore } from '~/stores/terminal';
import { asyncComputed } from '@vueuse/core';
import { usePopularDirectionsStore } from '~/stores/popular-directions';
import { getCityByTranslit } from '~/api/city';
import ContactsFilials from '~/components/public/contacts/contacts-filials.vue';
import PublicServiceLinksGrid from '~/components/public/public-service-links-grid.vue'
import TerminalsGeo from '~/components/public/terminals/terminals-geo.vue'


definePageMeta({
  path: '/:tag(address|contacts)/:location?',
})

const { locationByGuid, setFrom } = useCityStore()
const { changeOneLocation } = usePopularDirectionsStore()
const { currentCity, rclCityList } = storeToRefs(useCityStore())
const { seo } = storeToRefs(useSeoStore())
const { shippingTypesItems } = storeToRefs(useShippingTypesStore())
const { terminalList } = storeToRefs(useTerminalStore())

const route = useRoute()
const router = useRouter()
const paramLocation = route.params?.location as string
const queryLocationGuid = route.query?.locationGuid as string
const queryTerminalAddress = route.query?.terminal as string
const contactCity = await getContactCity()

const items = computed(() => {
  return [
    {
      title: 'Расчет грузоперевозки',
      text: `Рассчитайте стоимость перевозки груза в г. ${contactCity.name} и из г. ${contactCity.name} и сделайте заказ`,
      link: {
        to: `/order/create/${contactCity.translit}/`
      }
    },
    ...shippingTypesItems.value[0].links
  ]
})

const isAddressPage = computed(() => {
  return !!(route.params.tag === 'address' && !paramLocation)
})

const isAddressLocationPage = computed(() => {
  return !!(route.params.tag === 'address' && paramLocation)
})

async function getContactCity() {
  const location = queryLocationGuid || paramLocation
  if (location) {
    if (route.params.tag === 'address') {
      const result = rclCityList.value.find(c => c.translit === location) || 
        (await getCityByTranslit({
          city: location,
          path: route.path,
          orderWay: `${location}_${location}`,
          isOneLocation: false
        }))?.from

        if (!result?.guid) {
          showError({statusCode: 404})
        }
        return result
    }
    return await locationByGuid(location)
  }
  if (queryTerminalAddress) {
    await until(() => !!terminalList.value.length).toBe(true)
    const terminal = terminalList.value.find(t => t.address.trim() === queryTerminalAddress)
    if (terminal) {
      return await locationByGuid(terminal.location_guid)
    }
  }
  return currentCity.value
}

const title = computed(() => {
  return isAddressPage.value
    ? 'Транспортная компания «Возовоз» - филиалы в России и СНГ' 
    : isAddressLocationPage.value
      ? `Транспортная компания «Возовоз» в ${contactCity?.prepositional_name || ('г. ' + contactCity?.name )}`
      : `Контакты г. ${contactCity?.name}`
})

const titleInBreadcrumbs = computed(() => {
  return isAddressPage.value
    ? 'Адреса филиалов' 
    : isAddressLocationPage.value
      ? contactCity.name
      : ''
})

const popularTitle = computed(() => {
  return isAddressLocationPage.value
    ? `Направления грузоперевозок по г. ${contactCity.name}`
    : undefined
})

function onClick(location: Location) {
  router.push({ name: 'contacts', params: { tag: 'address', location: location.translit } })
}

// ssr
await until(() => !!terminalList.value.length && !!contactCity?.guid).toBe(true)
  .then(async () => {
    await changeOneLocation(contactCity.guid)
    
    if (isAddressLocationPage.value) {
      setFrom(contactCity)
    }
  })
</script>
